import { useEffect, useRef } from "react";
import { useHotkeysContext } from "react-hotkeys-hook";

import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { useAppSelector } from "store/hooks";

const viewerObjectFromBarcode = (barcode: string) => {
  const split = barcode.replace("<", "").split("-");
  return {
    object_type: split.at(0)?.toLowerCase(),
    object_id: Number.isNaN(Number(split.at(1)))
      ? undefined
      : Number(split.at(1)),
  };
};
type ViewerBarcodeObject = ReturnType<typeof viewerObjectFromBarcode>;

const useViewerBarcodeScanner = (
  callback: (barcode: ViewerBarcodeObject) => false | void
) => {
  const { disableScope, enableScope } = useHotkeysContext();
  const viewerBarcodeScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "viewerBarcodeScanning")
  );
  const open = useRef(false);
  const inProgressBarcode = useRef("");

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const endCharacter = {
      key: "Enter",
      code: "Enter",
    };
    const escCharacter = {
      key: "Escape",
      code: "Escape",
    };

    const isActiveInput = () => {
      switch (document.activeElement?.tagName) {
        case "TEXTAREA":
        case "SELECT":
          return true;
        case "INPUT":
          return document.activeElement?.getAttribute("type") === "radio" ||
            document.activeElement?.getAttribute("type") === "checkbox"
            ? false
            : true;
        default:
          return false;
      }
    };

    const captureKeyboard = (e: KeyboardEvent) => {
      if (
        !viewerBarcodeScanning ||
        isActiveInput() ||
        e.key === escCharacter.key ||
        e.code === escCharacter.code
      ) {
        inProgressBarcode.current = "";
        open.current = false;
        enableScope("viewerHotkeys");
        return;
      }
      if (open.current) {
        e.preventDefault();
        e.stopPropagation();
        disableScope("viewerHotkeys");
        if (e.key === endCharacter.key || e.code === endCharacter.code) {
          open.current = false;
          enableScope("viewerHotkeys");

          const object = viewerObjectFromBarcode(inProgressBarcode.current);
          const callbackResult = callbackRef.current(object);
          inProgressBarcode.current = "";
        } else {
          if (e.key !== "Shift")
            inProgressBarcode.current = inProgressBarcode.current + e.key;
        }
      } else {
        if (e.key === "<") {
          open.current = true;
          e.preventDefault();
          e.stopPropagation();
          disableScope("viewerHotkeys");
          console.log("disabled scope");
          inProgressBarcode.current = "<";

          // Unfocus any "button", "radio", or "checkbox" element
          if (
            document.activeElement?.tagName === "BUTTON" ||
            (document.activeElement?.tagName === "INPUT" &&
              (document.activeElement?.getAttribute("type") === "radio" ||
                document.activeElement?.getAttribute("type") === "checkbox"))
          ) {
            (
              document.activeElement as HTMLButtonElement | HTMLInputElement
            ).blur();
          }
        }
      }
    };

    if (viewerBarcodeScanning)
      document.addEventListener("keydown", captureKeyboard);

    return () => {
      document.removeEventListener("keydown", captureKeyboard);
    };
  }, [viewerBarcodeScanning]);
};

export default useViewerBarcodeScanner;
